import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import Localize from 'react-intl-universal';
import { Link } from 'react-router-dom';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import RichTextEditor from '@components/RichTextEditor/RichTextEditor';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { downloadTemplate } from '@pages/EventDetails/Util';

const StyledCancelButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: '700',
  color: theme.palette.primary.main
}));

const ConsentManagementForm = ({
  setIsCheckoutFormValid = () => {},
  listOfConsents = [],
  checkedConsents = {},
  setCheckedConsents = () => {}
}) => {
  const [selectedConsentDescription, setSelectedConsentDescription] = useState(null);
  const [openConsentDialog, setOpenConsentDialog] = useState(false);
  const [capVal, setCapValue] = useState(null);
  const [contextData] = useGlobalContext();
  const theme = useTheme();

  const enableRecaptcha = import.meta.env.VITE_ENABLE_RECAPTCHA === 'true';

  const handleCheckboxChange = (consentId) => (event) => {
    setCheckedConsents((prev) => ({
      ...prev,
      [consentId]: event.target.checked
    }));
  };

  const handleOpenDescription = (description, fileURL, url, fileName) => {
    if (url) {
      window.open(url, '_blank');
    } else if (fileURL) {
      if (fileURL.startsWith('http')) {
        window.open(fileURL, '_blank');
      } else {
        downloadTemplate(fileName, fileURL);
      }
    } else {
      setSelectedConsentDescription(description);
      setOpenConsentDialog(true);
    }
  };

  const handleCloseDescription = () => {
    setOpenConsentDialog(false);
  };

  useEffect(() => {
    const allObligatoryChecked = listOfConsents?.length
      ? listOfConsents?.every((consent) => {
          // If consent is not obligatory then it does not need to be checked.
          if (!consent?.mandatory) return true;
          return checkedConsents[consent?.id] === true;
        })
      : true;
    enableRecaptcha
      ? setIsCheckoutFormValid(allObligatoryChecked && capVal !== null)
      : setIsCheckoutFormValid(allObligatoryChecked);
  }, [checkedConsents, capVal]);

  return (
    <>
      <FormGroup>
        {listOfConsents?.map(
          ({ id, name, shortDescription, description, mandatory, fileURL, url, fileName }) => (
            <FormControlLabel
              key={id}
              required={mandatory}
              style={{ pointerEvents: 'none' }}
              control={
                <Checkbox
                  checked={checkedConsents[id] || false}
                  onChange={handleCheckboxChange(id)}
                  style={{ pointerEvents: 'auto' }}
                />
              }
              label={
                <>
                  <Typography variant='span' fontWeight='bold' sx={{ pr: 1 }}>
                    {shortDescription || ''}
                  </Typography>
                  <Link
                    component='button'
                    type='button'
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenDescription(description || '', fileURL, url, fileName);
                    }}
                    style={{ pointerEvents: 'auto', color: theme?.palette?.booking?.checkBox }}
                  >
                    {name || ''}
                  </Link>
                </>
              }
            />
          )
        )}
      </FormGroup>

      {import.meta.env.VITE_ENABLE_RECAPTCHA === 'true' ? (
        <ReCAPTCHA
          key={contextData.colorMode}
          sitekey={import.meta.env.VITE_API_RECAPTCHA_SITEKEY}
          onChange={(val) => setCapValue(val)}
          theme={contextData.colorMode || 'light'}
        />
      ) : null}

      <Dialog open={openConsentDialog} onClose={handleCloseDescription} maxWidth='md'>
        <DialogContent sx={{ minWidth: '20rem' }}>
          <RichTextEditor
            initialValue={selectedConsentDescription}
            emptyTextMessage={Localize.get('EventDetails.noTopics')}
            disabled
            isToolbarHidden
          />
        </DialogContent>
        <DialogActions>
          <StyledCancelButton onClick={handleCloseDescription}>
            {Localize.get('App.close')}
          </StyledCancelButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ConsentManagementForm;
